import React, { useState, useEffect } from "react";
import Sidebar from "./Sidebar";
import Header from "./Header";
import Notification from "./Notification";
import { FiUsers, FiEdit, FiTrash2, FiUser, FiMail, FiPhone, FiShield, FiX, FiPlus, FiBriefcase, FiLock, FiEye, FiEyeOff, FiCheck, FiHash, FiKey } from "react-icons/fi";
import { FaSortAlphaDown, FaSortAlphaUp, FaSortNumericDown, FaSortNumericUp } from "react-icons/fa";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const UserManagement = () => {
  const [sidebarOpen, setSidebarOpen] = useState(() => (typeof window !== "undefined" ? window.innerWidth >= 1024 : true));
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editUser, setEditUser] = useState({
    id_user: "",
    full_name: "",
    username: "",
    password: "",
    company_id: "",
    company_name: "",
    billing_account_id: "",
    role: "",
    email: "",
    phone: "",
  });
  const [addUserForm, setAddUserForm] = useState({
    full_name: "",
    username: "",
    password: "",
    email: "",
    phone: "",
    company_id: "",
    company_name: "",
    role: "",
    billing_account_id: "",
  });
  const [selectedRole, setSelectedRole] = useState(null);
  const [sortBy, setSortBy] = useState("full_name");
  const [sortOrder, setSortOrder] = useState("asc");
  const user = JSON.parse(localStorage.getItem("user"));
  const role = user ? user.role : null;
  const navigate = useNavigate();

  const roleOptions = [
    { value: "Admin", label: "Admin" },
    { value: "Customer Admin", label: "Customer Admin" },
    { value: "Customer", label: "Customer" },
  ];

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        const companyId = user ? user.company_id : null;
        if (companyId) {
          const userResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/company/${companyId}`);
          setUsers(userResponse.data);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const closeNotification = () => setNotification(null);
  const handleEditUser = (u) => {
    setEditUser({
      id_user: u.id_user,
      full_name: u.full_name || "",
      username: u.username || "",
      password: "",
      company_id: u.company_id || "",
      company_name: u.company_name || "",
      billing_account_id: u.billing_account_id || "",
      role: u.role || "",
      email: u.email || "",
      phone: u.phone || "",
    });
    setShowEditPassword(false);
    setIsEditModalOpen(true);
  };
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditUser((prevState) => ({ ...prevState, [name]: value }));
  };
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setIsSavingEdit(true);
    // Kirim semua field yang dibutuhkan backend
    const payload = {
      role: editUser.role || user.role,
      full_name: editUser.full_name,
      username: editUser.username,
      email: editUser.email,
      phone: editUser.phone,
      company_id: editUser.company_id,
      company_name: editUser.company_name || user.company_name,
    };
    if (editUser.password && editUser.password.trim() !== "") {
      payload.password = editUser.password;
    }
    try {
      await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/users/${editUser.id_user}`, payload);
      setNotification({ type: "success", message: "User updated successfully!" });
      setUsers(users.map((u) => (u.id_user === editUser.id_user ? { ...u, ...payload } : u)));
      setIsEditModalOpen(false);
    } catch (error) {
      setNotification({ type: "error", message: error?.response?.data?.message || "Failed to update user. Please try again." });
    } finally {
      setIsSavingEdit(false);
    }
  };
  const handleDeleteUser = async (id_user) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/users/${id_user}`);
      setNotification({ type: "success", message: "User deleted successfully!" });
      setUsers(users.filter((user) => user.id_user !== id_user));
    } catch (error) {
      setNotification({ type: "error", message: "Failed to delete user. Please try again." });
    }
  };
  const confirmDeleteUser = async () => {
    if (userToDelete) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/users/${userToDelete}`);
        setNotification({ type: "success", message: "User deleted successfully!" });
        setUsers(users.filter((user) => user.id_user !== userToDelete));
      } catch (error) {
        setNotification({ type: "error", message: "Failed to delete user. Please try again." });
      }
      setIsDeleteModalOpen(false);
    }
  };
  const openDeleteModal = (id_user) => {
    setUserToDelete(id_user);
    setIsDeleteModalOpen(true);
  };
  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setUserToDelete(null);
  };
  const toggleSidebar = () => setSidebarOpen((prevState) => !prevState);
  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "Admin":
        return "bg-red-100 text-red-800 border-red-200";
      case "Customer Admin":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "Customer":
        return "bg-green-100 text-green-800 border-green-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };
  const handleAddUserChange = (e) => {
    const { name, value } = e.target;
    setAddUserForm((prev) => ({ ...prev, [name]: value }));
  };
  const handleAddUserRoleChange = (option) => {
    setSelectedRole(option);
    setAddUserForm((prev) => ({ ...prev, role: option ? option.value : "" }));
  };
  const handleAddUserSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/users/`, addUserForm);
      setNotification({ type: "success", message: "User added successfully!" });
      setIsAddUserModalOpen(false);
      setAddUserForm({
        full_name: "",
        username: "",
        password: "",
        email: "",
        phone: "",
        company_id: user ? user.company_id : "",
        company_name: user ? user.company_name : "",
        role: "",
        billing_account_id: user ? user.billing_account_id : "",
      });
      // Refresh user list
      const userResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/company/${user.company_id}`);
      setUsers(userResponse.data);
    } catch (error) {
      setNotification({ type: "error", message: error?.response?.data?.message || "Failed to add user. Please try again." });
    }
  };
  const sortedUsers = [...users].sort((a, b) => {
    if (sortBy === "full_name") {
      if (!a.full_name) return 1;
      if (!b.full_name) return -1;
      return sortOrder === "asc"
        ? a.full_name.localeCompare(b.full_name)
        : b.full_name.localeCompare(a.full_name);
    } else if (sortBy === "role") {
      if (!a.role) return 1;
      if (!b.role) return -1;
      return sortOrder === "asc"
        ? a.role.localeCompare(b.role)
        : b.role.localeCompare(a.role);
    } else if (sortBy === "email") {
      if (!a.email) return 1;
      if (!b.email) return -1;
      return sortOrder === "asc"
        ? a.email.localeCompare(b.email)
        : b.email.localeCompare(a.email);
    } else if (sortBy === "company_name") {
      if (!a.company_name) return 1;
      if (!b.company_name) return -1;
      return sortOrder === "asc"
        ? a.company_name.localeCompare(b.company_name)
        : b.company_name.localeCompare(a.company_name);
    } else if (sortBy === "billing_account_id") {
      if (!a.billing_account_id) return 1;
      if (!b.billing_account_id) return -1;
      return sortOrder === "asc"
        ? a.billing_account_id.localeCompare(b.billing_account_id, undefined, { numeric: true })
        : b.billing_account_id.localeCompare(a.billing_account_id, undefined, { numeric: true });
    }
    return 0;
  });
  if (isLoading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />
        <div className="flex flex-1 bg-gray-100 min-w-0 max-w-full overflow-x-hidden">
          <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
          <div className={`flex-1 min-w-0 max-w-full overflow-x-hidden p-3 sm:p-6 transition-all duration-300 ${sidebarOpen ? "lg:ml-64" : "lg:ml-20"} ml-0`}>
            <div className="pt-20 pb-6">
              <div className="mb-8">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-purple-50 to-violet-50 px-4 sm:px-6 py-4 border-b border-gray-200">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                      <div className="flex items-center">
                        <div className="bg-purple-100 p-2 rounded-lg mr-3">
                          <FiUsers className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <h2 className="text-xl font-semibold text-gray-900">User Management</h2>
                          <p className="text-sm text-gray-600">View and manage user accounts and permissions</p>
                        </div>
                      </div>
                      <button className="flex items-center justify-center bg-purple-600 text-white py-2 px-4 rounded-lg shadow-sm" disabled>
                        <FiPlus className="mr-2 w-4 h-4" />
                        Add User
                      </button>
                    </div>
                  </div>
                  <div className="overflow-x-auto w-full">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Billing ID</th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                    </table>
                    <div className="bg-white">
                      <div className="divide-y divide-gray-200">
                        {[...Array(5)].map((_, index) => (
                          <div key={index} className="flex">
                            <div className="px-6 py-4 w-1/6">
                              <div className="flex items-center">
                                <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse mr-3"></div>
                                <div>
                                  <div className="h-4 bg-gray-200 rounded w-24 mb-2 animate-pulse"></div>
                                  <div className="h-3 bg-gray-100 rounded w-16 animate-pulse"></div>
                                </div>
                              </div>
                            </div>
                            <div className="px-6 py-4 w-1/6">
                              <div className="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
                            </div>
                            <div className="px-6 py-4 w-1/6">
                              <div className="h-4 bg-gray-200 rounded w-28 mb-2 animate-pulse"></div>
                              <div className="h-4 bg-gray-100 rounded w-20 animate-pulse"></div>
                            </div>
                            <div className="px-6 py-4 w-1/6">
                              <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                            </div>
                            <div className="px-6 py-4 w-1/6">
                              <div className="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
                            </div>
                            <div className="px-6 py-4 w-1/6 flex justify-center">
                              <div className="h-8 bg-gray-200 rounded w-16 animate-pulse"></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col min-h-screen">
      <Header toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />
      <div className="flex flex-1 bg-gray-100 min-w-0 max-w-full overflow-x-hidden">
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <div className={`flex-1 min-w-0 max-w-full overflow-x-hidden p-3 sm:p-6 transition-all duration-300 ${sidebarOpen ? "lg:ml-64" : "lg:ml-20"} ml-0`}>
          <div className="pt-20 pb-6">
            <div className="mb-8">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-purple-50 to-violet-50 px-4 sm:px-6 py-4 border-b border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                    <div className="flex items-center">
                      <div className="bg-purple-100 p-2 rounded-lg mr-3">
                        <FiUsers className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900">User Management</h2>
                        <p className="text-sm text-gray-600">View and manage user accounts and permissions</p>
                      </div>
                    </div>
                    <button onClick={() => navigate("/add-users")} className="flex items-center justify-center bg-purple-600 text-white py-2 px-4 rounded-lg shadow-sm hover:bg-purple-700 transition-all duration-200 hover:shadow-md">
                      <FiPlus className="mr-2 w-4 h-4" />
                      Add User
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto w-full">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <div className="flex items-center space-x-1">
                            User
                            <button
                              className="ml-1 text-gray-400 hover:text-purple-600"
                              onClick={() => {
                                setSortBy("full_name");
                                setSortOrder(sortBy === "full_name" && sortOrder === "asc" ? "desc" : "asc");
                              }}
                            >
                              {sortBy === "full_name" && sortOrder === "asc" ? (
                                <FaSortAlphaDown className="inline w-4 h-4" />
                              ) : (
                                <FaSortAlphaUp className="inline w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <div className="flex items-center space-x-1">
                            Role
                            <button
                              className="ml-1 text-gray-400 hover:text-purple-600"
                              onClick={() => {
                                setSortBy("role");
                                setSortOrder(sortBy === "role" && sortOrder === "asc" ? "desc" : "asc");
                              }}
                            >
                              {sortBy === "role" && sortOrder === "asc" ? (
                                <FaSortAlphaDown className="inline w-4 h-4" />
                              ) : (
                                <FaSortAlphaUp className="inline w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <div className="flex items-center space-x-1">
                            Contact
                            <button
                              className="ml-1 text-gray-400 hover:text-purple-600"
                              onClick={() => {
                                setSortBy("email");
                                setSortOrder(sortBy === "email" && sortOrder === "asc" ? "desc" : "asc");
                              }}
                            >
                              {sortBy === "email" && sortOrder === "asc" ? (
                                <FaSortAlphaDown className="inline w-4 h-4" />
                              ) : (
                                <FaSortAlphaUp className="inline w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <div className="flex items-center space-x-1">
                            Company
                            <button
                              className="ml-1 text-gray-400 hover:text-purple-600"
                              onClick={() => {
                                setSortBy("company_name");
                                setSortOrder(sortBy === "company_name" && sortOrder === "asc" ? "desc" : "asc");
                              }}
                            >
                              {sortBy === "company_name" && sortOrder === "asc" ? (
                                <FaSortAlphaDown className="inline w-4 h-4" />
                              ) : (
                                <FaSortAlphaUp className="inline w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <div className="flex items-center space-x-1">
                            Billing ID
                            <button
                              className="ml-1 text-gray-400 hover:text-purple-600"
                              onClick={() => {
                                setSortBy("billing_account_id");
                                setSortOrder(sortBy === "billing_account_id" && sortOrder === "asc" ? "desc" : "asc");
                              }}
                            >
                              {sortBy === "billing_account_id" && sortOrder === "asc" ? (
                                <FaSortNumericDown className="inline w-4 h-4" />
                              ) : (
                                <FaSortNumericUp className="inline w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sortedUsers.map((user) => (
                        <tr key={user.id_user} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="bg-gray-100 p-2 rounded-full mr-3">
                                <FiUser className="w-4 h-4 text-gray-600" />
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">{user.full_name}</div>
                                <div className="text-sm text-gray-500">@{user.username}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full border ${getRoleBadgeColor(user.role)}`}><FiShield className="w-3 h-3 mr-1" />{user.role}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              <div className="flex items-center mb-1"><FiMail className="w-3 h-3 mr-1 text-gray-400" />{user.email}</div>
                              <div className="flex items-center"><FiPhone className="w-3 h-3 mr-1 text-gray-400" />{user.phone}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.company_name}</td>
                          <td className="px-6 py-4 whitespace-nowrap"><span className="font-mono text-sm text-gray-600">{user.billing_account_id}</span></td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex justify-center space-x-2">
                              <button className="text-blue-600 hover:text-blue-800 p-1 rounded-md hover:bg-blue-50 transition-colors" onClick={() => handleEditUser(user)}><FiEdit className="w-4 h-4" /></button>
                              <button className="text-red-600 hover:text-red-800 p-1 rounded-md hover:bg-red-50 transition-colors" onClick={() => openDeleteModal(user.id_user)}><FiTrash2 className="w-4 h-4" /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {notification && (<Notification type={notification.type} message={notification.message} onClose={closeNotification} />)}
      {/* Edit User Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 font-sans animate-in fade-in duration-150">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-700 via-indigo-700 to-indigo-800 text-white px-6 py-5 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3.5">
                <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white font-bold text-lg shadow-sm">
                  {editUser.full_name
                    ? editUser.full_name
                        .split(" ")
                        .filter(Boolean)
                        .map((n) => n[0])
                        .slice(0, 2)
                        .join("")
                        .toUpperCase()
                    : <FiUser className="w-6 h-6" />}
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-lg sm:text-xl font-bold tracking-tight text-white">Edit User Profile</h3>
                    {editUser.role && (
                      <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-indigo-500/30 text-indigo-100 border border-indigo-400/30">
                        {editUser.role}
                      </span>
                    )}
                  </div>
                  <p className="text-blue-100 text-xs font-medium mt-0.5">
                    {editUser.email || editUser.username || "Update account credentials and system privileges"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsEditModalOpen(false)}
                className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors cursor-pointer"
                title="Close"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Form Content */}
            <form onSubmit={handleEditSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="p-6 overflow-y-auto space-y-5 flex-1 bg-slate-50/50">
                {/* Section 1: Personal Details */}
                <div className="bg-white rounded-xl p-5 border border-slate-200/80 shadow-xs space-y-4">
                  <div className="flex items-center gap-2 border-b border-slate-100 pb-2.5">
                    <div className="p-1.5 bg-blue-50 text-blue-600 rounded-lg">
                      <FiUser className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Personal Information</h4>
                      <p className="text-[11px] text-slate-400 font-normal">Basic identity and contact details</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="full_name"
                        value={editUser.full_name}
                        onChange={handleEditChange}
                        required
                        placeholder="e.g. Robi Firmansyah"
                        className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Username <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="username"
                        value={editUser.username}
                        onChange={handleEditChange}
                        required
                        placeholder="e.g. robifirmansyah"
                        className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={editUser.email}
                        onChange={handleEditChange}
                        required
                        placeholder="user@example.com"
                        className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={editUser.phone}
                        onChange={handleEditChange}
                        required
                        placeholder="e.g. 08123456789"
                        className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                      />
                    </div>
                  </div>
                </div>

                {/* Section 2: Organization Association (Read-Only Badges) */}
                <div className="bg-white rounded-xl p-5 border border-slate-200/80 shadow-xs space-y-3.5">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-2.5">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg">
                        <FiBriefcase className="w-4 h-4" />
                      </div>
                      <div>
                        <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Company & Billing Scope</h4>
                        <p className="text-[11px] text-slate-400 font-normal">Organization assignment (Read-only)</p>
                      </div>
                    </div>
                    <span className="flex items-center gap-1 text-[11px] font-semibold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-lg">
                      <FiLock className="w-3 h-3 text-slate-400" />
                      Managed at Org Level
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <div className="bg-slate-50 border border-slate-200/70 rounded-xl p-3 flex flex-col justify-between">
                      <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5 mb-1.5">
                        <FiBriefcase className="w-3.5 h-3.5 text-indigo-600" />
                        Company ID
                      </span>
                      <span className="font-mono text-xs font-bold text-slate-900 bg-white px-2.5 py-1.5 rounded-lg border border-slate-200">
                        {editUser.company_id || "—"}
                      </span>
                    </div>

                    <div className="bg-slate-50 border border-slate-200/70 rounded-xl p-3 flex flex-col justify-between">
                      <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5 mb-1.5">
                        <FiHash className="w-3.5 h-3.5 text-blue-600" />
                        Billing Account ID
                      </span>
                      <span className="font-mono text-xs font-bold text-slate-900 bg-white px-2.5 py-1.5 rounded-lg border border-slate-200">
                        {editUser.billing_account_id || "—"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Section 3: Access & Security */}
                <div className="bg-white rounded-xl p-5 border border-slate-200/80 shadow-xs space-y-4">
                  <div className="flex items-center gap-2 border-b border-slate-100 pb-2.5">
                    <div className="p-1.5 bg-purple-50 text-purple-600 rounded-lg">
                      <FiShield className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Access Control & Security</h4>
                      <p className="text-[11px] text-slate-400 font-normal">Role permissions and authentication</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Assigned Role <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="role"
                        value={editUser.role || ""}
                        onChange={handleEditChange}
                        required
                        className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition cursor-pointer"
                      >
                        <option value="">Select a role</option>
                        {roleOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <p className="text-[11px] text-slate-400 mt-1">Determines system capabilities and portal access level</p>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showEditPassword ? "text" : "password"}
                          name="password"
                          value={editUser.password || ""}
                          onChange={handleEditChange}
                          placeholder="Leave blank to keep current"
                          className="w-full pl-3.5 pr-10 py-2.5 bg-white border border-slate-300 rounded-xl text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                        />
                        <button
                          type="button"
                          onClick={() => setShowEditPassword(!showEditPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition p-1 cursor-pointer"
                          title={showEditPassword ? "Hide password" : "Show password"}
                        >
                          {showEditPassword ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                        </button>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-1">Leave empty to keep existing password untouched</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="bg-white border-t border-slate-200 px-6 py-4 flex flex-wrap items-center justify-between gap-3 shrink-0">
                <span className="text-xs text-slate-500 flex items-center gap-1.5">
                  <FiCheck className="w-3.5 h-3.5 text-emerald-600" />
                  Changes apply immediately upon saving
                </span>

                <div className="flex items-center gap-2.5">
                  <button
                    type="button"
                    onClick={() => setIsEditModalOpen(false)}
                    className="px-4 py-2 text-slate-700 bg-slate-100 hover:bg-slate-200 font-semibold rounded-xl text-xs sm:text-sm transition-all cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSavingEdit}
                    className="px-5 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-xl shadow-xs hover:shadow text-xs sm:text-sm transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSavingEdit ? "Saving Changes..." : "Save Changes"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Delete User Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 font-sans animate-in fade-in duration-150">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in zoom-in-95 duration-150">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mr-4 shrink-0">
                <FiTrash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Delete User</h3>
                <p className="text-xs text-gray-500">This action will revoke all system access</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this user? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteUser}
                className="px-5 py-2 text-sm font-semibold bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors shadow-xs cursor-pointer"
              >
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Add User Modal */}
      {isAddUserModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 font-sans animate-in fade-in duration-150">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-700 via-indigo-700 to-indigo-800 text-white px-6 py-5 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3.5">
                <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white shadow-sm">
                  <FiPlus className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold tracking-tight text-white">Create New User</h3>
                  <p className="text-blue-100 text-xs font-medium mt-0.5">
                    Assign credentials, contact details, and platform access role
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsAddUserModalOpen(false)}
                className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors cursor-pointer"
                title="Close"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAddUserSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="p-6 overflow-y-auto space-y-5 flex-1 bg-slate-50/50">
                {/* Section 1: Personal Details */}
                <div className="bg-white rounded-xl p-5 border border-slate-200/80 shadow-xs space-y-4">
                  <div className="flex items-center gap-2 border-b border-slate-100 pb-2.5">
                    <div className="p-1.5 bg-blue-50 text-blue-600 rounded-lg">
                      <FiUser className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Personal Information</h4>
                      <p className="text-[11px] text-slate-400 font-normal">Basic identity and contact details</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="full_name"
                        value={addUserForm.full_name}
                        onChange={handleAddUserChange}
                        className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                        placeholder="e.g. John Doe"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Username <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="username"
                        value={addUserForm.username}
                        onChange={handleAddUserChange}
                        className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                        placeholder="e.g. johndoe"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={addUserForm.email}
                        onChange={handleAddUserChange}
                        className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                        placeholder="john@example.com"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Phone <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={addUserForm.phone}
                        onChange={handleAddUserChange}
                        className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                        placeholder="08123456789"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Section 2: Organization Association (Read-Only Badges) */}
                <div className="bg-white rounded-xl p-5 border border-slate-200/80 shadow-xs space-y-3.5">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-2.5">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg">
                        <FiBriefcase className="w-4 h-4" />
                      </div>
                      <div>
                        <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Company & Billing Scope</h4>
                        <p className="text-[11px] text-slate-400 font-normal">Inherited from current organization</p>
                      </div>
                    </div>
                    <span className="flex items-center gap-1 text-[11px] font-semibold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-lg">
                      <FiLock className="w-3 h-3 text-slate-400" />
                      Auto-Assigned
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <div className="bg-slate-50 border border-slate-200/70 rounded-xl p-3 flex flex-col justify-between">
                      <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5 mb-1.5">
                        <FiBriefcase className="w-3.5 h-3.5 text-indigo-600" />
                        Company ID
                      </span>
                      <span className="font-mono text-xs font-bold text-slate-900 bg-white px-2.5 py-1.5 rounded-lg border border-slate-200">
                        {addUserForm.company_id || "—"}
                      </span>
                    </div>

                    <div className="bg-slate-50 border border-slate-200/70 rounded-xl p-3 flex flex-col justify-between">
                      <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5 mb-1.5">
                        <FiHash className="w-3.5 h-3.5 text-blue-600" />
                        Billing Account ID
                      </span>
                      <span className="font-mono text-xs font-bold text-slate-900 bg-white px-2.5 py-1.5 rounded-lg border border-slate-200">
                        {addUserForm.billing_account_id || "—"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Section 3: Access & Security */}
                <div className="bg-white rounded-xl p-5 border border-slate-200/80 shadow-xs space-y-4">
                  <div className="flex items-center gap-2 border-b border-slate-100 pb-2.5">
                    <div className="p-1.5 bg-purple-50 text-purple-600 rounded-lg">
                      <FiShield className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Access Control & Security</h4>
                      <p className="text-[11px] text-slate-400 font-normal">Role assignment and initial credentials</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Role <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="role"
                        value={addUserForm.role}
                        onChange={handleAddUserChange}
                        className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition cursor-pointer"
                        required
                      >
                        <option value="">Select a role</option>
                        {roleOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      <p className="text-[11px] text-slate-400 mt-1">Determines system capabilities and portal access level</p>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Password <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="password"
                        name="password"
                        value={addUserForm.password}
                        onChange={handleAddUserChange}
                        className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                        placeholder="Enter secure initial password"
                        required
                      />
                      <p className="text-[11px] text-slate-400 mt-1">Temporary password provided to user upon creation</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="bg-white border-t border-slate-200 px-6 py-4 flex flex-wrap items-center justify-between gap-3 shrink-0">
                <span className="text-xs text-slate-500 flex items-center gap-1.5">
                  <FiCheck className="w-3.5 h-3.5 text-emerald-600" />
                  Account will be activated immediately
                </span>

                <div className="flex items-center gap-2.5">
                  <button
                    type="button"
                    onClick={() => setIsAddUserModalOpen(false)}
                    className="px-4 py-2 text-slate-700 bg-slate-100 hover:bg-slate-200 font-semibold rounded-xl text-xs sm:text-sm transition-all cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-xl shadow-xs hover:shadow text-xs sm:text-sm transition-all cursor-pointer"
                  >
                    Add User
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;